import { OverlayEventDetail } from '@ionic/core';
import { PopoverController } from '@ionic/angular';
import { Component, Input, OnInit } from '@angular/core';

import { Asset } from '@services/assets/asset.interfaces';
import { IProperty } from '@services/property/property-model.interfaces';
import { CustomFormComponent } from '@services/formBuilder/abstract-custom-form-field';
import { ITreeViewModalConfig, TreeViewNodeIdType } from '@shared/modules/tree-viewer/models';
import { FolderPickerService } from '@modules/management/modules/folders/services/folder-picker.service';
import { ContentFolderService } from '@modules/management/pages/list/content/services/content-folder.service';
import {
  AssetsService,
  ExternalEventsService,
  FoldersDataService,
  IExternalEventItem,
  ObjectItem,
  ObjectsService,
  PropertyService
} from '@services';
import {
  ViewModalTreePopupComponent
} from '@shared/modules/tree-viewer/view-modals/view-modal-popup/view-modal-popup.component';
import {
  FolderDataType,
  FolderPickerConfig,
  FolderPickerEntities,
  FolderPickerInput,
  FolderPickerSelection, SelectedFolderItem
} from '@modules/management/modules/folders/model/folders.interfaces';

import { assign, each, filter, find, includes, join, map, split, values } from 'lodash';

@Component({
  selector: 'app-folder-picker',
  templateUrl: './folder-picker.component.html',
  styleUrls: ['./folder-picker.component.scss'],
})
export class FolderPickerComponent extends CustomFormComponent implements OnInit {
  @Input() set selectedIds(ids: FolderPickerSelection[]) {
    if (ids) {
      this.selectedEntities = ids;
    }
  }
  @Input() folderPickerInput: FolderPickerInput;
  public selectedItems: SelectedFolderItem[] = [];

  private folderEntities: FolderPickerEntities = {};
  private selectedEntities: FolderPickerSelection[];

  constructor(
    private assetsService: AssetsService,
    private folderPickerService: FolderPickerService,
    private popoverController: PopoverController,
    private foldersDataService: FoldersDataService,
    private objectsService: ObjectsService,
    private propertyService: PropertyService,
    private externalEventsService: ExternalEventsService,
    private contentFolderService: ContentFolderService
  ) {
    super();
  }

  ngOnInit() {
    // Handle case where formValue is a JSON string instead of an array
    if (typeof this.formValue === 'string') {
      try {
        this.selectedEntities = JSON.parse(this.formValue);
        this.emitChanges();
      } catch (e) {
        console.error('FolderPicker: Failed to parse formValue as JSON:', e);
        this.selectedEntities = [];
      }
    } else {
      this.selectedEntities ??= this.formValue;
    }

    this.defineSelectedItems();
  }

  public showTree() {
    this.popoverController.create({
      component: ViewModalTreePopupComponent,
      animated: false,
      componentProps: {
        config: <ITreeViewModalConfig>{
          title: this.getTreeViewTitle(),
          nodes: this.folderPickerService.getTreeNodes(this.getTreeConfigByType()),
          multiselect: this.folderPickerInput?.viewTree?.multiselect ?? true,
          disableParentSelection: this.folderPickerInput?.viewTree?.disableParentSelection ?? false
        }
      }
    }).then((element: HTMLIonPopoverElement) => {
      element.present();

      element.onDidDismiss().then((event: OverlayEventDetail<TreeViewNodeIdType[]>) => {
        if (event.data) {
          this.onSelection(event.data);
        }
      });
    });
  }

  private emitChanges() {
    this.formValue = this.selectedEntities;
  }

  public resetSelection() {
    this.onSelection([]);
  }

  private getTreeConfigByType(): FolderPickerConfig {
    const sourceData = this.folderPickerInput?.viewTree?.getSourceItems?.();
    const config: FolderPickerConfig = {
      dataType: this.folderPickerInput.type,
      entities: this.folderEntities,
      selectedIds: this.selectedEntities,
      parentIds: [],
      getFolderItems: () => [],
      getItemId: (item: FolderPickerSelection) => item?.itemID,
      getFolderId: (folder) => folder?.folderID
    };

    if (this.folderPickerInput.type === FolderDataType.ASSET) {
      assign(config, {
        selectAllLabel: 'MGMT_LIST.All_Assets',
        getFolderItems: () => sourceData || this.assetsService.getEnabledAssets(),
        getItemId: (asset: Asset) => asset?.assetID,
        getItemTitle: (asset: Asset) => asset?.name,
      });
    } else if (this.folderPickerInput.type === FolderDataType.CONTENT) {
      config.parentIds = [
        ...(config.parentIds || []),
        ...(this.contentFolderService.getRestrictedFolderSelections() || [])
      ];

      assign(config, {
        getFolderItems: () => {
          const contentItems = this.objectsService.filterData(sourceData as ObjectItem[] || this.objectsService.getCachedObjectByAlias('content') || [], true);
          return this.contentFolderService.filterObjects(contentItems);
        },
        getItemId: (object: ObjectItem | FolderPickerSelection) => (object as ObjectItem)?.objectID || (object as FolderPickerSelection)?.itemID,
        getItemTitle: (object: ObjectItem) => object?.description || `Unknown (${object?.objectID})`,
      });
    } else if (this.folderPickerInput.type === FolderDataType.EXTERNAL_ITEMS) {
      assign(config, {
        selectAllLabel: 'EXTERNAL.All_Items',
        getFolderItems: () => this.folderPickerInput?.viewTree?.getSourceItems ? sourceData : this.externalEventsService.getItemsAsList(),
        getItemId: (item: IExternalEventItem) => item?.itemID,
        getItemTitle: (item: IExternalEventItem) => `${item.uniqueName}`,
      });
    } else if (this.folderPickerInput.type === FolderDataType.PROPERTY) {
      assign(config, {
        getFolderItems: () => sourceData || this.propertyService.getProperties(),
        getItemId: (property: IProperty | FolderPickerSelection) => (property as IProperty)?.propertyID || (property as FolderPickerSelection)?.itemID,
        getItemTitle: (property: IProperty) => property?.title,
      });
    }

    if (this.folderPickerInput?.viewTree?.folderConfig) {
      assign(config, this.folderPickerInput.viewTree.folderConfig);
    }

    return config;
  }

  private onSelection(data: TreeViewNodeIdType[]) {
    // Separate folders and items from the selection
    const folders: number[] = [];
    const items: number[] = [];

    each(data, (nodeId) => {
      const folderID = +split(nodeId as string, FolderPickerService.folderIdPrefix)?.[1];
      if (isNaN(folderID)) {
        items.push(+nodeId);
      } else {
        folders.push(folderID);
      }
    });

    if (this.folderPickerInput.type === FolderDataType.ASSET) {
      // Filter out assets that belong to selected folders
      const filteredAssets = filter(items, (assetID) => {
        const asset = this.assetsService.getAssetById(assetID)?.[0];
        if (!asset) {
          return false;
        }

        // Normalize folderID to 0 if null/undefined (root level)
        const assetFolderID = asset.folderID ?? 0;
        // Exclude this asset if its folder is selected
        return !includes(folders, assetFolderID);
      });

      this.selectedEntities = [
        ...map(folders, (folderID): FolderPickerSelection => ({ folderID })),
        ...map(filteredAssets, (assetID): FolderPickerSelection => ({ assetID }))
      ];
    } else {
      // Filter out items that belong to selected folders
      const config = this.getTreeConfigByType();
      let folderItems = values(config?.entities?.folderItems);
      folderItems = folderItems?.length ? folderItems : config?.getFolderItems?.();

      const filteredItems = filter(items, (itemID) => {
        const item = find(folderItems, (folderItem) => {
          return config?.getItemId(folderItem) === itemID;
        });

        if (!item) {
          return false;
        }

        // Normalize folderID to 0 if null/undefined (root level)
        const itemFolderID = item.folderID ?? 0;
        // Exclude this item if its folder is selected
        return !includes(folders, itemFolderID);
      });

      this.selectedEntities = [
        ...map(folders, (folderID): FolderPickerSelection => ({ folderID })),
        ...map(filteredItems, (itemID): FolderPickerSelection => ({ itemID }))
      ];
    }

    this.defineSelectedItems();
    this.emitChanges();
  }

  private defineSelectedItems() {
    const config = this.getTreeConfigByType();
    this.selectedItems = filter(map(this.selectedEntities, (selectedEntity) => {
      let selectedItem = { title: '', isFolder: false };

      if (selectedEntity.assetID) {
        const asset = this.assetsService.getAssetById(selectedEntity.assetID)?.[0];
        if (asset) {
          selectedItem.title = asset.name;
        } else {
          // Asset not found - skip this entry
          return;
        }
      } else if (selectedEntity.itemID) {
        let folderItems = values(config?.entities?.folderItems);
        folderItems = folderItems?.length ? folderItems : config?.getFolderItems?.();

        const currentItem = find(folderItems, (folderItem) => {
          return config?.getItemId(folderItem) === selectedEntity.itemID;
        });

        if (currentItem) {
          selectedItem.title = this.folderPickerInput?.selectedList?.itemLabel?.(currentItem) ?? config?.getItemTitle(currentItem);
        } else {
          // Item not found in folder items - show Unknown with ID
          selectedItem.title = `Unknown (${selectedEntity.itemID})`;
        }
      } else if (selectedEntity.folderID !== undefined) {
        // Special case: folderID === 0 means "All Assets" or "All Items"
        if (selectedEntity.folderID === 0) {
          selectedItem.title = config.selectAllLabel || 'SHARED.All';
          selectedItem.isFolder = true;
        } else if (this.foldersDataService.isActive(selectedEntity.folderID)) {
          selectedItem.title = join(this.foldersDataService.getFolderPath(selectedEntity.folderID, true), '/ ');
          selectedItem.isFolder = true;
        } else {
          const isActive = this.foldersDataService.isActive(selectedEntity.folderID);
          if (isActive) {
            const folderPath = this.foldersDataService.getFolderPath(selectedEntity.folderID, true);
            selectedItem.title = join(folderPath, '/ ');
            selectedItem.isFolder = true;
          } else {
            // this item shouldn't be here - it is not selectable
            return;
          }
        }
      } else {
        selectedItem.title = config.selectAllLabel || 'SHARED.All';
        selectedItem.isFolder = true;
      }

      return selectedItem;
    }), 'title');
  }

  private getTreeViewTitle(): string {
    let title = '';

    if (this.folderPickerInput.type === FolderDataType.ASSET) {
      title = 'MGMT_DETAILS.Asset_Targets';
    } else if (this.folderPickerInput.type === FolderDataType.PROPERTY) {
      title = 'PROPERTY.Properties';
    }

    if (this.folderPickerInput?.viewTree?.title) {
      title = this.folderPickerInput.viewTree.title;
    }

    return title;
  }
}
